import React, { useState } from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import { useAppSettings } from '../hooks/useAppSettings';
import ProCalculatorFooter from './ProCalculatorFooter';
import SectionHelp from './SectionHelp';
import ToggleSwitch from './ToggleSwitch';
import CookieAIReview from './CookieAIReview';
import { lengthToDisplay, lengthFromDisplay, weightToDisplay, lengthUnitLabel, areaUnitLabel, areaToDisplay, weightUnitLabel } from '../utils/unitConversion';
import { lengthUnitWarning } from '../utils/unitWarnings';
import '../styles/ProCalculator.css';
import '../styles/DimensionsSection.css';

const DimensionsSection = () => {
  const {
    dimensionsRows,
    updateDimensionsRow,
    updateCookieRow,
    cookieRows,
    showCalculationFields,
    setShowCalculationFields,
    isMetric,
    setIsMetric,
    isMetricWeight,
    sortDimensionsRows,
    isMobile,
    setCurrentAnalyzingRow,
    setShowImageAnalyzer,
    goToNextSection,
    activeSection,
    setActiveSection,
    loadedPlanName,
    showAdvanced,
    updateAdvancedFeatures,
    user
  } = useProCalculator();

  const { settings: appSettings } = useAppSettings();
  const aiModel = appSettings?.color_mix_ai_model || 'Claude (Anthropic)';

  // Show area column always if showCalculationFields, OR if any row uses Override Area
  const anyRowOverride = dimensionsRows.some(r => r.shapeType === 'override');
  const showAreaColumn = showCalculationFields || anyRowOverride;

  // Unit-mismatch warnings: set on blur, cleared on any keystroke
  const [warnings, setWarnings] = useState({});
  const wKey = (rowId, field) => `${rowId}_${field}`;
  const clearWarning = (rowId, field) => {
    const k = wKey(rowId, field);
    setWarnings(prev => { if (!prev[k]) return prev; const next = { ...prev }; delete next[k]; return next; });
  };
  const evalWarning = (rowId, field, value) => {
    const k = wKey(rowId, field);
    const w = lengthUnitWarning(value, isMetric);
    setWarnings(prev => w ? { ...prev, [k]: w } : (({ [k]: _, ...rest }) => rest)(prev));
  };

  return (
    <div className="dimensions-section">
      {/* Header with title and plan name */}
      <div className="section-header">
        <h2 className="section-title">Size & Shape</h2>
        {loadedPlanName && <span className="loaded-plan-name">Plan: {loadedPlanName}</span>}
      </div>

      {/* Help text container */}
      <div className="section-help-container">
        <SectionHelp sectionId="dimensions" isMobile={isMobile} />
      </div>

      {/* Unit preference box */}
      <div className="unit-prefs-box">
        <div className="unit-prefs-card">
          <h3 className="unit-prefs-title">Measurement Units</h3>
          <p className="unit-prefs-desc">Choose whether you measure cookie dimensions in inches or millimeters.</p>
          <div className="unit-toggles">
            <div className="unit-toggle-group">
              <label className="reference-toggle-label">inches</label>
              <ToggleSwitch isOn={isMetric} onToggle={(val) => setIsMetric(val)} />
              <label className="reference-toggle-label">mm</label>
            </div>
          </div>
        </div>

        {user && (
          <div className="unit-prefs-card">
            <h3 className="unit-prefs-title unit-prefs-title--precision">Calculation Detail</h3>
            <p className="unit-prefs-desc">Simple covers rectangles, squares, and circles. Advanced adds custom area override and photo/STL-based shape detection.</p>
            <div className="unit-toggles">
              <div className="unit-toggle-group unit-toggle-group--advanced">
                <label className="reference-toggle-label">Simple</label>
                <ToggleSwitch isOn={showAdvanced} onToggle={(val) => updateAdvancedFeatures(val)} />
                <label className="reference-toggle-label advanced-label">
                  <span className="advanced-label-full">Advanced</span>
                  <span className="advanced-label-short">Adv</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {!user && (
          <div className="unit-prefs-card">
            <p className="unit-prefs-desc unit-prefs-signin-hint">
              <a href="/login?tab=signup&redirect=/pro">Sign in free</a> to unlock Advanced mode and AI-powered tools.
            </p>
          </div>
        )}
      </div>

      {/* Actions container for buttons */}
      <div className="section-actions-container">
        {showAdvanced && !isMobile && (
          <button
            onClick={() => setShowCalculationFields(!showCalculationFields)}
            className={`btn-secondary ${showCalculationFields ? 'active' : ''}`}
            title="Toggle calculation fields"
          >
            {showCalculationFields ? 'Hide Calculations' : 'Show Calculations'}
          </button>
        )}
      </div>
      
      <div className={`dimensions-table ${showCalculationFields ? 'show-calculations' : ''}`}>
        {!isMobile && (
          <div className={`dimensions-table-header ${showCalculationFields ? 'show-calculations' : ''}`}>
            <div className="cookie-name-header">Cookie Name</div>
            <div className="shape-type-header">Shape Type</div>
            <div className="dimensions-header">Dimensions</div>
            {showCalculationFields && showAreaColumn && (
              <div className="area-header">Cookie Area ({areaUnitLabel(isMetric)})</div>
            )}
            {showCalculationFields && (
              <div className="icing-header">Icing per Cookie ({weightUnitLabel(isMetricWeight)})</div>
            )}
          </div>
        )}
        
        {dimensionsRows.map(row => {
          // Find the corresponding cookie row
          const cookie = cookieRows.find(c => c.id === row.cookieId);

          return (
            <div key={row.id} className={`dimensions-row ${showCalculationFields ? 'show-calculations' : ''}`}>
              <div className="cookie-name-field">
                {cookie ? (
                  <input
                    type="text"
                    value={cookie.cookieName || ''}
                    placeholder="Cookie Name"
                    onChange={(e) => updateCookieRow(cookie.id, 'cookieName', e.target.value)}
                    onFocus={(e) => { const t = e.target; setTimeout(() => t.select(), 0); }}
                    className="cookie-name-input dim-cookie-name-input"
                  />
                ) : (
                  <em>Need Cookie Name</em>
                )}
              </div>
              
              <div className="shape-type-field">
                <select
                  value={row.shapeType}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateDimensionsRow(row.id, 'shapeType', value);
                    // Clear dimensions when changing shape type
                    if (value === 'circle') {
                      updateDimensionsRow(row.id, 'width', '');
                      updateDimensionsRow(row.id, 'length', '');
                    } else if (value === 'square') {
                      updateDimensionsRow(row.id, 'diameter', '');
                      updateDimensionsRow(row.id, 'length', '');
                    } else if (value === 'override' || value === 'photo') {
                      updateDimensionsRow(row.id, 'width', '');
                      updateDimensionsRow(row.id, 'length', '');
                      updateDimensionsRow(row.id, 'diameter', '');
                    } else {
                      updateDimensionsRow(row.id, 'diameter', '');
                    }
                  }}
                  className="shape-type-select"
                >
                  <option value="">Select shape</option>
                  <option value="rectangle">Rectangle</option>
                  <option value="square">Square</option>
                  <option value="circle">Circle</option>
                  {/* Show Override Area if Advanced mode OR this row already uses it */}
                  {(showAdvanced || row.shapeType === 'override') && (
                    <option value="override">Enter Area Directly</option>
                  )}
                  {/* Show Photo / STL if Advanced mode OR this row already uses it */}
                  {(showAdvanced || row.shapeType === 'photo') && (
                    <option value="photo">Photo / STL</option>
                  )}
                </select>
              </div>
              
              <div className="dimensions-fields">
                {row.shapeType === 'square' && (
                  <div className="dimension-field">
                    <label>Side Length:</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={lengthToDisplay(row.width, isMetric)}
                      onChange={(e) => { clearWarning(row.id, 'width'); updateDimensionsRow(row.id, 'width', lengthFromDisplay(e.target.value, isMetric)); }}
                      onFocus={(e) => { const t = e.target; setTimeout(() => t.select(), 0); }}
                      onBlur={(e) => evalWarning(row.id, 'width', e.target.value)}
                      className="dimension-input"
                    />
                    <span className="unit-label">{lengthUnitLabel(isMetric)}</span>
                    {warnings[wKey(row.id, 'width')] && (
                      <div className="unit-warning">{warnings[wKey(row.id, 'width')]}</div>
                    )}
                  </div>
                )}
                
                {(row.shapeType === 'rectangle' || row.shapeType === 'triangle' || row.shapeType === 'custom') && (
                  <>
                    <div className="dimension-field">
                      <label>{row.shapeType === 'triangle' ? 'Max Width:' : row.shapeType === 'custom' ? 'Max Width:' : 'Width:'}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={lengthToDisplay(row.width, isMetric)}
                        onChange={(e) => { clearWarning(row.id, 'width'); updateDimensionsRow(row.id, 'width', lengthFromDisplay(e.target.value, isMetric)); }}
                        onFocus={(e) => { const t = e.target; setTimeout(() => t.select(), 0); }}
                        onBlur={(e) => evalWarning(row.id, 'width', e.target.value)}
                        className="dimension-input"
                      />
                      <span className="unit-label">{lengthUnitLabel(isMetric)}</span>
                      {warnings[wKey(row.id, 'width')] && (
                        <div className="unit-warning">{warnings[wKey(row.id, 'width')]}</div>
                      )}
                    </div>
                    
                    <div className="dimension-field">
                      <label>{row.shapeType === 'custom' ? 'Max Length:' : 'Length:'}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={lengthToDisplay(row.length, isMetric)}
                        onChange={(e) => { clearWarning(row.id, 'length'); updateDimensionsRow(row.id, 'length', lengthFromDisplay(e.target.value, isMetric)); }}
                        onFocus={(e) => { const t = e.target; setTimeout(() => t.select(), 0); }}
                        onBlur={(e) => evalWarning(row.id, 'length', e.target.value)}
                        className="dimension-input"
                      />
                      <span className="unit-label">{lengthUnitLabel(isMetric)}</span>
                      {warnings[wKey(row.id, 'length')] && (
                        <div className="unit-warning">{warnings[wKey(row.id, 'length')]}</div>
                      )}
                    </div>
                  </>
                )}
                
                {row.shapeType === 'custom' && (
                  <div className="analyze-button-container">
                    <button 
                      onClick={() => {
                        setCurrentAnalyzingRow(row);
                        setShowImageAnalyzer(true);
                      }}
                      className="analyze-button"
                    >
                      Analyze Image
                    </button>
                  </div>
                )}
                
                {row.shapeType === 'circle' && (
                  <div className="dimension-field">
                    <label>Diameter:</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={lengthToDisplay(row.diameter, isMetric)}
                      onChange={(e) => { clearWarning(row.id, 'diameter'); updateDimensionsRow(row.id, 'diameter', lengthFromDisplay(e.target.value, isMetric)); }}
                      onBlur={(e) => evalWarning(row.id, 'diameter', e.target.value)}
                      className="dimension-input"
                    />
                    <span className="unit-label">{lengthUnitLabel(isMetric)}</span>
                    {warnings[wKey(row.id, 'diameter')] && (
                      <div className="unit-warning">{warnings[wKey(row.id, 'diameter')]}</div>
                    )}
                  </div>
                )}

                {row.shapeType === 'override' && (
                  <div className="dimension-field">
                    <label>Cookie Area:</label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={row.overrideArea !== '' && row.overrideArea !== undefined ? areaToDisplay(row.overrideArea, isMetric) : ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') { updateDimensionsRow(row.id, 'overrideArea', ''); return; }
                        const displayVal = parseFloat(raw);
                        if (isNaN(displayVal)) return;
                        const CM_PER_INCH = 2.54;
                        const sqIn = isMetric ? displayVal / (CM_PER_INCH * CM_PER_INCH) : displayVal;
                        updateDimensionsRow(row.id, 'overrideArea', sqIn);
                      }}
                      className="dimension-input"
                    />
                    <span className="unit-label">{areaUnitLabel(isMetric)}</span>
                  </div>
                )}

                {row.shapeType === 'photo' && (
                  <CookieAIReview
                    rowId={String(row.id)}
                    isMetric={isMetric}
                    aiModel={aiModel}
                    onAcceptArea={(sqIn, aiNotes, usedAI) => {
                      updateDimensionsRow(row.id, 'overrideArea', sqIn);
                      updateDimensionsRow(row.id, 'aiNotes', aiNotes);
                      updateDimensionsRow(row.id, 'usedAI', usedAI);
                    }}
                  />
                )}

                {row.shapeType === 'photo' && row.overrideArea > 0 && (
                  <div className="dimension-field dim-field--ai-area">
                    <label>
                      {row.usedAI ? 'AI Area:' : 'Area:'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={areaToDisplay(row.overrideArea, isMetric).toFixed(3)}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') { updateDimensionsRow(row.id, 'overrideArea', ''); return; }
                        const displayVal = parseFloat(raw);
                        if (isNaN(displayVal)) return;
                        const CM_PER_INCH = 2.54;
                        const sqIn = isMetric ? displayVal / (CM_PER_INCH * CM_PER_INCH) : displayVal;
                        updateDimensionsRow(row.id, 'overrideArea', sqIn);
                      }}
                      className="dimension-input"
                    />
                    <span className="unit-label">{areaUnitLabel(isMetric)}</span>
                  </div>
                )}

              </div>

              {/* Area column — only shown when Show Calculations is on */}
              {showCalculationFields && showAreaColumn && row.shapeType && (
                <div className="calculation-field area-field">
                  <label className="mobile-only-label">Cookie Area:</label>
                  <span className="calculation-value">
                    {row.area > 0 ? `${areaToDisplay(row.area, isMetric).toFixed(3)} ${areaUnitLabel(isMetric)}` : '-'}
                  </span>
                </div>
              )}

              {showCalculationFields && row.shapeType && (
                <div className="calculation-field icing-field"> 
                  <label className="mobile-only-label">Icing per Cookie:</label>
                  <span className="calculation-value">{weightToDisplay(row.icingNeeded || 0, isMetricWeight).toFixed(2)} {weightUnitLabel(isMetricWeight)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Footer with Save/Save As/Clear and Next buttons */}
      {activeSection !== 'all' && (
        <ProCalculatorFooter
          onBack={() => setActiveSection('cookieNames')}
          backLabel="Back: Cookie Names"
          onNext={() => goToNextSection('dimensions')}
          nextLabel="Next: Colors"
        />
      )}
    </div>
  );
};

export default DimensionsSection;
